import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/supabase-context';

const ToolsDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleToolClick = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const familyTools = [
    {
      title: 'KindlyMe Card',
      description: 'Share your needs and preferences with businesses',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-teal">
          <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
          <circle cx="9" cy="10" r="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M15 9H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M15 13H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M6 16H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      path: '/kindlyme',
      requiresAuth: false
    },
    {
      title: 'IEP/504 Meeting Prep',
      description: 'Organize goals, concerns & questions before your meeting',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-teal">
          <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <rect x="9" y="3" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 12H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M9 16H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      path: '/tools/iep-meeting-prep',
      requiresAuth: false
    },
    {
      title: 'Doctor Visit Prep',
      description: 'Reduce anxiety with step-by-step medical visit guides',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-teal">
          <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8 15v1a6 6 0 0 0 6 6a6 6 0 0 0 6-6v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="20" cy="10" r="2" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      path: '/tools/doctor-visit-prep',
      requiresAuth: false
    },
    {
      title: 'Visit Prep Kit',
      description: 'Personalized social stories & packing lists for visiting places',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-teal">
          <path d="M4 19.5C4 18.837 4.26339 18.2011 4.73223 17.7322C5.20107 17.2634 5.83696 17 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6.5 2H20V22H6.5C5.83696 22 5.20107 21.7366 4.73223 21.2678C4.26339 20.7989 4 20.163 4 19.5V4.5C4 3.83696 4.26339 3.20107 4.73223 2.73223C5.20107 2.26339 5.83696 2 6.5 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      path: '/tools/visit-prep-kit',
      requiresAuth: false
    },
    {
      title: 'Story Creator',
      description: 'AI-powered social stories for routines, emotions & life changes',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-teal">
          <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      path: '/tools/story-creator',
      requiresAuth: false
    }
  ];

  const guides = [
    {
      title: 'First 90 Days After Diagnosis',
      description: 'Practical guidance for families navigating a new diagnosis',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-teal">
          <path d="M4 19.5C4 18.837 4.26339 18.2011 4.73223 17.7322C5.20107 17.2634 5.83696 17 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6.5 2H20V22H6.5C5.83696 22 5.20107 21.7366 4.73223 21.2678C4.26339 20.7989 4 20.163 4 19.5V4.5C4 3.83696 4.26339 3.20107 4.73223 2.73223C5.20107 2.26339 5.83696 2 6.5 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 7H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M9 11H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M9 15H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      path: '/guides/first-90-days',
      requiresAuth: false
    }
  ];

  const businessTools = [
    {
      title: 'Accessibility Self-Assessment',
      description: 'Score your space and get actionable improvements',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-purple">
          <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      path: '/tools/accessibility-assessment',
      requiresAuth: false
    },
    {
      title: 'Staff Training Guide',
      description: 'Teach your team to create welcoming experiences',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-purple">
          <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      path: '/tools/staff-training',
      requiresAuth: false
    }
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-gray-700 hover:text-[#4BB0A8] px-3 py-2 text-sm font-medium transition-colors duration-200 flex items-center space-x-1"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Tools menu"
      >
        <span>Tools</span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-white rounded-lg shadow-xl z-50 border border-gray-200" style={{ width: '680px' }}>
          {/* FOR FAMILIES header */}
          <div className="px-5 pt-3 pb-1.5">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#4BB0A8]">
              For Families
            </p>
          </div>

          {/* Family tools + guide in two columns */}
          <div className="grid grid-cols-2 gap-x-1 px-2 pb-1">
            {/* Left column: tools */}
            <div>
              <p className="px-3 pt-1 pb-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">
                Tools
              </p>
              {familyTools.slice(0, 3).map((tool, index) => {
                const needsAuth = tool.requiresAuth && !isAuthenticated;
                return (
                  <button
                    key={index}
                    onClick={() => needsAuth ? navigate('/login', { state: { from: tool.path } }) : handleToolClick(tool.path)}
                    className="w-full px-3 py-2 rounded-md hover:bg-gray-50 transition-colors duration-150 flex items-start gap-2.5 focus:outline-none focus:bg-gray-50"
                  >
                    <div className="flex-shrink-0 mt-0.5">{tool.icon}</div>
                    <div className="text-left min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                        {tool.title}
                        {needsAuth && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">Login</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">{tool.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Right column: more tools */}
            <div>
              <p className="px-3 pt-1 pb-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">
                &nbsp;
              </p>
              {familyTools.slice(3).map((tool, index) => {
                const needsAuth = tool.requiresAuth && !isAuthenticated;
                return (
                  <button
                    key={index}
                    onClick={() => needsAuth ? navigate('/login', { state: { from: tool.path } }) : handleToolClick(tool.path)}
                    className="w-full px-3 py-2 rounded-md hover:bg-gray-50 transition-colors duration-150 flex items-start gap-2.5 focus:outline-none focus:bg-gray-50"
                  >
                    <div className="flex-shrink-0 mt-0.5">{tool.icon}</div>
                    <div className="text-left min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                        {tool.title}
                        {needsAuth && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">Login</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">{tool.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Guides row under families */}
          <div className="px-2 pb-2">
            <p className="px-3 pt-1 pb-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-400 border-t border-gray-100">
              <span className="relative top-[-1px] inline-block pt-1.5">Guides</span>
            </p>
            {guides.map((guide, index) => (
              <button
                key={index}
                onClick={() => handleToolClick(guide.path)}
                className="w-full px-3 py-2 rounded-md hover:bg-gray-50 transition-colors duration-150 flex items-start gap-2.5 focus:outline-none focus:bg-gray-50"
              >
                <div className="flex-shrink-0 mt-0.5">{guide.icon}</div>
                <div className="text-left min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{guide.title}</p>
                  <p className="text-xs text-gray-500">{guide.description}</p>
                </div>
                <span className="flex-shrink-0 mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#4BB0A8] bg-[#4BB0A8]/10 px-2 py-0.5 rounded-full">
                  Free
                </span>
              </button>
            ))}
          </div>

          {/* FOR BUSINESSES section */}
          <div className="border-t border-gray-100 px-5 pt-2.5 pb-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#9256A0]">
              For Businesses
            </p>
          </div>
          <div className="grid grid-cols-2 gap-x-1 px-2 pb-3">
            {businessTools.map((tool, index) => (
              <button
                key={index}
                onClick={() => handleToolClick(tool.path)}
                className="w-full px-3 py-2 rounded-md hover:bg-gray-50 transition-colors duration-150 flex items-start gap-2.5 focus:outline-none focus:bg-gray-50"
              >
                <div className="flex-shrink-0 mt-0.5">{tool.icon}</div>
                <div className="text-left min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{tool.title}</p>
                  <p className="text-xs text-gray-500">{tool.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolsDropdown;
